import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Dialog, Slider } from '@mui/material';
import { IDU_COLUMNS, POINT_OPTIONS, POINT_LABELS } from '../constants';
import { messages } from '../../common/notification';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const CONTROLLABLE_COLS = IDU_COLUMNS.filter(c => c.controllable);
const DEFAULT_POINT = CONTROLLABLE_COLS[0]?.key || '';
const REV_DAY = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' };

// ─── Tiny helpers ─────────────────────────────────────────────────────────────

function Radio({ checked, onClick, label }) {
  return (
    <label onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500, color: '#374151', userSelect: 'none' }}>
      <div style={{
        width: 18, height: 18, borderRadius: '50%',
        border: `2px solid ${checked ? '#2050ff' : '#d1d5db'}`,
        background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {checked && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#2050ff' }} />}
      </div>
      {label}
    </label>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>
      {children}
    </div>
  );
}

function getValueLabel(pointName, val) {
  if (val === null || val === undefined) return null;
  if (pointName === 'SetTemperature') return `${val}°C`;
  return POINT_LABELS[pointName]?.[val] ?? val;
}

// ─── Main component ────────────────────────────────────────────────────────────

function VRVScheduleModal({ open, onClose, idus, gateways = [], onSubmit, loading, editSchedule = null }) {
  const [name, setName] = useState('');
  const [time, setTime] = useState('');
  const [repeatMode, setRepeatMode] = useState('every');
  const [days, setDays] = useState([...DAYS]);
  const [pointName, setPointName] = useState(DEFAULT_POINT);
  const [value, setValue] = useState(DEFAULT_POINT === 'SetTemperature' ? 24 : null);
  const [search, setSearch] = useState('');
  const [date, setDate] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [errors, setErrors] = useState({});
  const [collapsedFloors, setCollapsedFloors] = useState(new Set());
  const [popover, setPopover] = useState(null); // { floor, idus, x, y }
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (editSchedule) {
      setName(editSchedule.name || '');
      if (editSchedule.type === 'cron') {
        setRepeatMode('every');
        const parts = editSchedule.expression.split(' ');
        const hr = String(parts[1]).padStart(2, '0');
        const min = String(parts[0]).padStart(2, '0');
        setTime(`${hr}:${min}`);
        setDate('');
        const dayStr = parts[4];
        let parsedDays;
        if (!dayStr || dayStr === '*' || dayStr === '?') {
          parsedDays = [...DAYS];
        } else if (dayStr.includes('-')) {
          const [s, e] = dayStr.split('-').map(Number);
          parsedDays = Array.from({ length: e - s + 1 }, (_, i) => REV_DAY[i + s]).filter(Boolean);
        } else {
          parsedDays = dayStr.split(',').map(Number).map(n => REV_DAY[n]).filter(Boolean);
        }
        setDays(parsedDays);
      } else {
        setRepeatMode('once');
        const [datePart, timePart] = editSchedule.expression.split('T');
        setDate(datePart || '');
        setTime(timePart?.substring(0, 5) || '');
        setDays([...DAYS]);
      }
      const action = editSchedule.actions?.[0]?.payload;
      const pn = action?.pointName || DEFAULT_POINT;
      setPointName(pn);
      setValue(action?.value ?? (pn === 'SetTemperature' ? 24 : null));
      const rawDevices = action?.devices || [];
      setSelectedIds(rawDevices.map(d => (typeof d === 'string' ? d : d.iduDeviceId)).filter(Boolean));
    } else {
      setName('');
      setTime('');
      setDate('');
      setRepeatMode('every');
      setDays([...DAYS]);
      setPointName(DEFAULT_POINT);
      setValue(DEFAULT_POINT === 'SetTemperature' ? 24 : null);
      setSelectedIds([]);
    }
    setSearch('');
    setErrors({});
    setCollapsedFloors(new Set());
  }, [open]);

  const toggleFloor = floor => setCollapsedFloors(prev => {
    const next = new Set(prev);
    next.has(floor) ? next.delete(floor) : next.add(floor);
    return next;
  });

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handlePointChange = key => {
    setPointName(key);
    setValue(key === 'SetTemperature' ? 24 : null);
    setErrors(p => ({ ...p, value: undefined }));
  };

  const toggleDay = day => {
    setDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
    setErrors(p => ({ ...p, days: undefined }));
  };

  const filteredIdus = search.trim()
    ? idus.filter(i =>
        i.name?.toLowerCase().includes(search.toLowerCase()) ||
        i.iduDeviceId?.toLowerCase().includes(search.toLowerCase()) ||
        i.latestStatus?.floorName?.toLowerCase().includes(search.toLowerCase())
      )
    : idus;

  // Group by floor
  const grouped = filteredIdus.reduce((acc, idu) => {
    const floor = idu.latestStatus?.floorName || idu.vrvGatewayId || 'Unknown Floor';
    if (!acc[floor]) acc[floor] = [];
    acc[floor].push(idu);
    return acc;
  }, {});

  const allFiltered = filteredIdus.every(i => selectedIds.includes(i.iduDeviceId));

  const handleSelectAll = () => {
    const ids = filteredIdus.map(i => i.iduDeviceId);
    if (allFiltered) {
      setSelectedIds(prev => prev.filter(id => !ids.includes(id)));
    } else {
      setSelectedIds(prev => [...new Set([...prev, ...ids])]);
    }
    setErrors(p => ({ ...p, idus: undefined }));
  };

  const handleToggleIdu = id => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    setErrors(p => ({ ...p, idus: undefined }));
  };

  const DAY_MAP = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = 'Schedule name is required';
    if (!time) e.time = 'Time is required';
    if (repeatMode === 'once' && !date) e.date = 'Date is required';
    if (repeatMode === 'every' && days.length === 0) e.days = 'Select at least one day';
    if (value === null || value === undefined) e.value = 'Select a value';
    if (selectedIds.length === 0) e.idus = 'Select at least one IDU';
    setErrors(e);
    if (Object.keys(e).length > 0) {
      const first = e.name || e.time || e.date || e.days || e.value || e.idus;
      messages.error(first);
    }
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const [hours, minutes] = time.split(':').map(Number);
    let expression;
    if (repeatMode === 'every') {
      const dayNums = days.map(d => DAY_MAP[d]).sort((a, b) => a - b).join(',');
      expression = `${minutes} ${hours} ? * ${dayNums} *`;
    } else {
      expression = `${date}T${String(hours).padStart(2,'0')}:${String(minutes).padStart(2,'0')}:00`;
    }
    onSubmit({
      name,
      type: repeatMode === 'every' ? 'cron' : 'at',
      expression,
      enabled: true,
      pointName,
      value,
      selectedIduIds: selectedIds,
    });
  };

  // ─── Value control ────────────────────────────────────────────────────────────

  const renderValueControl = () => {
    if (pointName === 'SetTemperature') {
      const temp = value ?? 24;
      return (
        <div style={{ paddingTop: 4 }}>
          <div style={{ textAlign: 'center', fontSize: 34, fontWeight: 800, color: '#2050ff', marginBottom: 2, letterSpacing: '-1px' }}>
            {temp}°C
          </div>
          <Slider
            min={18} max={30} step={1}
            value={temp}
            onChange={(_, v) => setValue(v)}
            valueLabelDisplay="auto"
            sx={{ color: '#2050ff', '& .MuiSlider-thumb': { width: 18, height: 18 }, '& .MuiSlider-rail': { opacity: 0.25 } }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#aaa', marginTop: 2 }}>
            <span>18°C</span><span>30°C</span>
          </div>
        </div>
      );
    }

    const opts = POINT_OPTIONS[pointName] || [];
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {opts.map(opt => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => { setValue(opt.value); setErrors(p => ({ ...p, value: undefined })); }}
              style={{
                padding: '8px 18px',
                border: `2px solid ${active ? '#2050ff' : '#e5e7eb'}`,
                borderRadius: 8,
                background: active ? 'rgba(32,80,255,0.08)' : '#fff',
                color: active ? '#2050ff' : '#6b7280',
                fontWeight: active ? 700 : 500,
                cursor: 'pointer',
                fontFamily: 'Manrope',
                fontSize: 13,
                transition: 'all 0.15s',
                outline: 'none',
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    );
  };

  const selectedDaysSummary = days.length === DAYS.length
    ? 'Every Day'
    : days.length === 0 ? '—' : days.join(', ');

  const commandSummary = getValueLabel(pointName, value);

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: { borderRadius: 18, fontFamily: 'Manrope', overflow: 'hidden', maxHeight: '92vh' },
      }}
    >
      {/* Dialog header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '18px 28px',
        borderBottom: '1px solid #f3f4f6',
      }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#111827', letterSpacing: '-0.3px' }}>
            {editSchedule ? 'Edit Schedule' : 'Create VRV Schedule'}
          </div>
          {commandSummary && selectedIds.length > 0 && (
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
              Set <strong style={{ color: '#2050ff' }}>{pointName.replace(/([A-Z])/g, ' $1').trim()}</strong> → <strong style={{ color: '#111' }}>{commandSummary}</strong> for <strong style={{ color: '#111' }}>{selectedIds.length}</strong> IDU{selectedIds.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 22, lineHeight: 1, padding: 4, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          ×
        </button>
      </div>

      {/* Body: two panels */}
      <div style={{ display: 'flex', height: 550 }}>

        {/* ── Left panel: schedule config ──────────────────────────────────── */}
        <div style={{
          width: '42%',
          padding: '20px 24px',
          borderRight: '1px solid #f3f4f6',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}>

          {/* Schedule Name */}
          <div>
            <SectionLabel>Schedule Name</SectionLabel>
            <input
              value={name}
              onChange={e => { setName(e.target.value); setErrors(p => ({ ...p, name: undefined })); }}
              placeholder="e.g. Morning Cool Down"
              style={{ ...inputStyle, borderColor: errors.name ? '#ef4444' : '#e5e7eb' }}
            />
            {errors.name && <div style={errorStyle}>{errors.name}</div>}
          </div>

          {/* Time */}
          <div>
            <SectionLabel>Select Time</SectionLabel>
            <input
              type="time"
              value={time}
              onChange={e => { setTime(e.target.value); setErrors(p => ({ ...p, time: undefined })); }}
              style={{ ...inputStyle, borderColor: errors.time ? '#ef4444' : '#e5e7eb' }}
            />
            {errors.time && <div style={errorStyle}>{errors.time}</div>}
          </div>

          {/* Repeat mode */}
          <div style={{ display: 'flex', gap: 24 }}>
            <Radio checked={repeatMode === 'every'} onClick={() => setRepeatMode('every')} label="Repeat" />
            <Radio checked={repeatMode === 'once'} onClick={() => setRepeatMode('once')} label="Once" />
          </div>

          {/* Date picker for repeat once */}
          {repeatMode === 'once' && (
            <div>
              <SectionLabel>Select Date</SectionLabel>
              <input
                type="date"
                value={date}
                onChange={e => { setDate(e.target.value); setErrors(p => ({ ...p, date: undefined })); }}
                style={{ ...inputStyle, borderColor: errors.date ? '#ef4444' : '#e5e7eb' }}
              />
              {errors.date && <div style={errorStyle}>{errors.date}</div>}
            </div>
          )}

          {/* Day picker */}
          {repeatMode === 'every' && (
            <div>
              <SectionLabel>Days</SectionLabel>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 10 }}>
                {DAYS.map(day => {
                  const active = days.includes(day);
                  return (
                    <button
                      key={day}
                      onClick={() => toggleDay(day)}
                      style={{
                        padding: '7px 11px',
                        border: 'none',
                        borderRadius: 8,
                        background: active ? '#111827' : '#f3f4f6',
                        color: active ? '#fff' : '#6b7280',
                        fontWeight: 600,
                        fontSize: 12,
                        cursor: 'pointer',
                        fontFamily: 'Manrope',
                        transition: 'all 0.15s',
                        outline: 'none',
                      }}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
              {errors.days && <div style={errorStyle}>{errors.days}</div>}
              {/* {days.length > 0 && (
                <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
                  <span style={{ fontWeight: 700, color: '#2050ff' }}>Every –</span> {selectedDaysSummary}
                </div>
              )} */}
            </div>
          )}

          {/* Divider */}
          <div style={{ borderTop: '1px solid #f3f4f6', margin: '0 -24px', padding: '0 24px' }} />

          {/* Point selector */}
          <div>
            <SectionLabel>Command Point</SectionLabel>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {CONTROLLABLE_COLS.map(col => {
                const active = pointName === col.key;
                return (
                  <button
                    key={col.key}
                    onClick={() => handlePointChange(col.key)}
                    style={{
                      padding: '6px 13px',
                      border: `2px solid ${active ? '#2050ff' : '#e5e7eb'}`,
                      borderRadius: 7,
                      background: active ? 'rgba(32,80,255,0.07)' : '#fafafa',
                      color: active ? '#2050ff' : '#6b7280',
                      fontWeight: active ? 700 : 500,
                      cursor: 'pointer',
                      fontFamily: 'Manrope',
                      fontSize: 12,
                      outline: 'none',
                      transition: 'all 0.15s',
                    }}
                  >
                    {col.header}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Value control */}
          <div>
            <SectionLabel>Value</SectionLabel>
            {renderValueControl()}
            {errors.value && <div style={errorStyle}>{errors.value}</div>}
          </div>
        </div>

        {/* ── Right panel: IDU selection ────────────────────────────────────── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>

          {/* Search + header */}
          <div style={{ padding: '20px 24px 0' }}>
            {/* Search */}
            <div style={{ position: 'relative', marginBottom: 14 }}>
              <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', display: 'flex', pointerEvents: 'none' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </span>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search IDUs..."
                style={{ ...inputStyle, paddingLeft: 32, paddingRight: 12 }}
              />
            </div>

            {/* Column header row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                Select Devices
                {selectedIds.length > 0 && (
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#2050ff', marginLeft: 8 }}>
                    {selectedIds.length} selected
                  </span>
                )}
              </span>
              <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <button onClick={handleSelectAll} style={linkBtnStyle}>
                  {allFiltered && filteredIdus.length > 0 ? 'Deselect All' : 'Select All'}
                </button>
                <span style={{ color: '#d1d5db', fontSize: 12 }}>/</span>
                <button onClick={() => setSelectedIds([])} style={linkBtnStyle}>Clear</button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '36px 1fr 90px', padding: '6px 4px', borderBottom: '2px solid #f3f4f6' }}>
              <div />
              <div style={colHeadStyle}>IDU Name</div>
              <div style={{ ...colHeadStyle, textAlign: 'right' }}>Floor</div>
            </div>
          </div>

          {/* Scrollable IDU list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px' }}>
            {filteredIdus.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#aaa', padding: '40px 0', fontSize: 13 }}>
                {idus.length === 0 ? 'Select a gateway first' : 'No IDUs match your search'}
              </div>
            ) : (
              Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true })).map(([floor, floorIdus]) => {
                const isCollapsed = collapsedFloors.has(floor);
                const floorAllSelected = floorIdus.every(i => selectedIds.includes(i.iduDeviceId));
                const floorSelectedCount = floorIdus.filter(i => selectedIds.includes(i.iduDeviceId)).length;
                const gwId = floorIdus[0]?.vrvGatewayId;
                const gw = gateways.find(g => g.vrvGatewayId === gwId);
                const isOnline = gw?.connectivityStatus === 'ONLINE';
                return (
                <div key={floor}>
                  {/* Group header */}
                  <div
                    style={{
                      fontSize: 11, fontWeight: 700, color: '#6b7280',
                      padding: '10px 4px 8px',
                      borderBottom: '1px solid #f3f4f6',
                      display: 'flex', alignItems: 'center', gap: 8,
                      letterSpacing: '0.05em', textTransform: 'uppercase',
                      userSelect: 'none',
                    }}
                  >
                    {/* Collapse toggle */}
                    <div onClick={() => toggleFloor(floor)} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: 1, minWidth: 0 }}>
                      <svg
                        width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                        style={{ transition: 'transform 0.2s', transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', flexShrink: 0 }}
                      >
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                      </svg>
                      {gw && (
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: isOnline ? '#22c55e' : '#ef4444', flexShrink: 0, display: 'inline-block' }} />
                      )}
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{floor}</span>
                      <span style={{ fontWeight: 400, color: '#bbb', textTransform: 'none', letterSpacing: 0, flexShrink: 0 }}>
                        ({floorIdus.length})
                      </span>
                      {floorSelectedCount > 0 && (
                        <span
                          onClick={e => {
                            e.stopPropagation();
                            const rect = e.currentTarget.getBoundingClientRect();
                            const selectedFloorIdus = floorIdus.filter(i => selectedIds.includes(i.iduDeviceId));
                            setPopover({ floor, idus: selectedFloorIdus, x: rect.left, y: rect.bottom + 6 });
                          }}
                          style={{ fontWeight: 700, color: '#2050ff', textTransform: 'none', letterSpacing: 0, flexShrink: 0, fontSize: 10, cursor: 'pointer', textDecoration: 'underline dotted' }}
                        >
                          {floorSelectedCount} selected
                        </span>
                      )}
                    </div>

                    {/* Floor-level select/deselect */}
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        const ids = floorIdus.map(i => i.iduDeviceId);
                        if (floorAllSelected) {
                          setSelectedIds(prev => prev.filter(id => !ids.includes(id)));
                        } else {
                          setSelectedIds(prev => [...new Set([...prev, ...ids])]);
                        }
                        setErrors(p => ({ ...p, idus: undefined }));
                      }}
                      style={{
                        padding: '3px 10px',
                        border: `1.5px solid ${floorAllSelected ? '#2050ff' : '#e5e7eb'}`,
                        borderRadius: 5,
                        background: floorAllSelected ? 'rgba(32,80,255,0.07)' : '#fff',
                        color: floorAllSelected ? '#2050ff' : '#9ca3af',
                        fontWeight: 600,
                        fontSize: 10,
                        fontFamily: 'Manrope',
                        cursor: 'pointer',
                        textTransform: 'none',
                        letterSpacing: 0,
                        flexShrink: 0,
                        transition: 'all 0.15s',
                        outline: 'none',
                      }}
                    >
                      {floorAllSelected ? '✓ All Selected' : 'Select Floor'}
                    </button>
                  </div>

                  {!isCollapsed && floorIdus.map(idu => {
                    const isChecked = selectedIds.includes(idu.iduDeviceId);
                    const statusOn = idu.latestStatus?.Status === 1;
                    return (
                      <div
                        key={idu.iduDeviceId}
                        onClick={() => handleToggleIdu(idu.iduDeviceId)}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '36px 1fr 90px',
                          alignItems: 'center',
                          padding: '9px 4px',
                          borderBottom: '1px solid #f9fafb',
                          cursor: 'pointer',
                          borderRadius: 6,
                          background: isChecked ? 'rgba(32,80,255,0.03)' : 'transparent',
                          transition: 'background 0.1s',
                        }}
                        onMouseEnter={e => { if (!isChecked) e.currentTarget.style.background = '#fafafa'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = isChecked ? 'rgba(32,80,255,0.03)' : 'transparent'; }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {}}
                            style={{ cursor: 'pointer', accentColor: '#2050ff', width: 14, height: 14 }}
                          />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: isChecked ? 600 : 400, color: '#1f2937' }}>
                              {idu.name || idu.iduDeviceId}
                            </div>
                            <div style={{ fontSize: 11, color: '#9ca3af' }}>
                              {idu.iduDeviceId}
                            </div>
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', fontSize: 11, color: statusOn ? '#16a34a' : '#9ca3af', fontWeight: 600 }}>
                          {statusOn ? 'ON' : 'OFF'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
              })
            )}
          </div>

          {/* Footer: error + create button */}
          <div style={{ padding: '12px 24px 20px', borderTop: '1px solid #f3f4f6' }}>
            {errors.idus && <div style={{ ...errorStyle, marginBottom: 8 }}>{errors.idus}</div>}
            <button
              onClick={handleSubmit}
              disabled={loading}
              style={{
                width: '100%',
                padding: '13px',
                border: 'none',
                borderRadius: 10,
                background: loading ? '#93a3d8' : '#111827',
                color: '#fff',
                fontWeight: 700,
                fontSize: 14,
                fontFamily: 'Manrope',
                cursor: loading ? 'not-allowed' : 'pointer',
                letterSpacing: '0.01em',
                transition: 'background 0.2s',
              }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.opacity = '0.85'; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
            >
              {loading ? (editSchedule ? 'Saving…' : 'Creating…') : (editSchedule ? 'Save Changes' : 'Create Schedule')}
            </button>
          </div>
        </div>
      </div>
    </Dialog>

    {popover && createPortal(
      <>
        {/* backdrop */}
        <div
          onClick={() => setPopover(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
        />
        {/* popover box */}
        <div
          ref={popoverRef}
          style={{
            position: 'fixed',
            top: Math.min(popover.y, window.innerHeight - 260),
            left: Math.min(popover.x, window.innerWidth - 240),
            zIndex: 9999,
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            width: 230,
            fontFamily: 'Manrope',
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: '10px 14px 8px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>
              {popover.floor}
            </span>
            <span style={{ fontSize: 11, color: '#2050ff', fontWeight: 600 }}>
              {popover.idus.length} IDU{popover.idus.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div style={{ maxHeight: 200, overflowY: 'auto', padding: '6px 0' }}>
            {popover.idus.map(idu => (
              <div key={idu.iduDeviceId} style={{ padding: '6px 14px', display: 'flex', flexDirection: 'column', gap: 1 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{idu.name || idu.iduDeviceId}</span>
                <span style={{ fontSize: 10, color: '#9ca3af' }}>{idu.iduDeviceId}</span>
              </div>
            ))}
          </div>
        </div>
      </>,
      document.body
    )}
    </>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────────

const inputStyle = {
  width: '100%',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: '9px 12px',
  fontSize: 13,
  fontFamily: 'Manrope',
  outline: 'none',
  color: '#374151',
  boxSizing: 'border-box',
  background: '#fff',
};

const errorStyle = {
  fontSize: 11,
  color: '#ef4444',
  marginTop: 4,
};

const linkBtnStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: '#2050ff',
  fontSize: 12,
  fontWeight: 600,
  fontFamily: 'Manrope',
  padding: '0 4px',
};

const colHeadStyle = {
  fontSize: 11,
  fontWeight: 700,
  color: '#9ca3af',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};

export default VRVScheduleModal;
